import React, { useEffect, useRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal, sankeyJustify } from 'd3-sankey';

interface SpendCubeChartProps {
  data: any[];
}

// Define the node interfaces for the Sankey diagram
interface SankeyNode {
  name: string;
  type: 'supplier' | 'category' | 'businessUnit';
  id: string;
  value?: number;
  index?: number;
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
}

interface SankeyLink {
  source: SankeyNode | number;
  target: SankeyNode | number;
  value: number;
  isFragmented?: boolean;
  uValue?: number; // Unique value for identification
  index?: number;
  width?: number;
  y0?: number;
  y1?: number;
}

const SpendCubeChart: React.FC<SpendCubeChartProps> = ({ data }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!data || data.length === 0 || !containerRef.current) {
      return;
    }
    
    // Clear any existing SVG
    d3.select(containerRef.current).select('svg').remove();
    
    // Create a local tooltip div if it doesn't exist
    let tooltipDiv: HTMLDivElement | null = null;
    
    // Set dimensions
    const margin = { top: 20, right: 30, bottom: 20, left: 30 };
    const width = containerRef.current.clientWidth - margin.left - margin.right;
    const height = containerRef.current.clientHeight - margin.top - margin.bottom;

    // Process data for the Sankey diagram
    const { nodes: sankeyNodes, links } = processDataForSankey(data);

    // Identify fragmented links (suppliers or BUs with multiple categories)
    identifyFragmentedLinks(links);

    // Create SVG
    const svg = d3.select(containerRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create tooltip
    let tooltip: d3.Selection<HTMLDivElement, unknown, null, undefined>;
    if (!tooltipRef.current) {
      tooltip = d3.select(containerRef.current)
        .append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('background-color', 'white')
        .style('border', '1px solid #ddd')
        .style('border-radius', '4px')
        .style('padding', '10px')
        .style('pointer-events', 'none')
        .style('font-size', '12px')
        .style('box-shadow', '0px 2px 6px rgba(0,0,0,0.1)');
      
      tooltipDiv = tooltip.node() || null;
      // Store the tooltip div in a ref without directly assigning to current
      tooltipRef.current = tooltipDiv;
    }

    // Set up the Sankey generator
    const sankeyGenerator = sankey<SankeyNode, SankeyLink>()
      .nodeWidth(20)
      .nodePadding(10)
      .extent([[0, 0], [width, height]])
      .nodeAlign(sankeyJustify);

    // Format the data for sankey diagram
    const sankeyData = {
      nodes: sankeyNodes,
      links: links
    };

    // Generate the sankey diagram
    sankeyGenerator(sankeyData);

    // Draw the links
    svg.append('g')
      .attr('fill', 'none')
      .attr('stroke-opacity', 0.5)
      .selectAll('path')
      .data(sankeyData.links)
      .join('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke', (d) => (d as any).isFragmented ? theme.palette.error.main : theme.palette.grey[400])
      .attr('stroke-width', (d) => Math.max(1, (d as any).width || 0))
      .on('mouseover', function(event, d) {
        // Highlight this link
        d3.select(this)
          .attr('stroke-opacity', 0.8)
          .attr('stroke-width', (d) => Math.max(1, (d as any).width || 0));
          
        // Show tooltip
        if (tooltipRef.current) {
          const tooltip = d3.select(tooltipRef.current);
          const dLink = d as any;
          
          const source = typeof dLink.source === 'object' ? dLink.source.name : 'Unknown';
          const target = typeof dLink.target === 'object' ? dLink.target.name : 'Unknown';
          const value = d3.format('$,.0f')(dLink.value);
          
          tooltip.html(`
            <div>
              <strong>${source} → ${target}</strong><br/>
              Spend: ${value}<br/>
              ${dLink.isFragmented ? '<span style="color: #f44336;">Fragmented Spend Path</span>' : ''}
            </div>
          `)
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY + 10}px`)
          .style('opacity', 1);
        }
      })
      .on('mouseout', function() {
        // Reset this link
        d3.select(this)
          .attr('stroke-opacity', 0.5)
          .attr('stroke-width', (d) => Math.max(1, (d as any).width || 0));
          
        // Hide tooltip
        if (tooltipRef.current) {
          d3.select(tooltipRef.current).style('opacity', 0);
        }
      });

    // Draw the nodes
    const nodeGroup = svg.append('g')
      .selectAll('g')
      .data(sankeyData.nodes)
      .join('g')
      .attr('transform', (d: SankeyNode) => `translate(${d.x0},${d.y0})`);

    // Add rectangles for the nodes
    nodeGroup.append('rect')
      .attr('height', (d) => ((d as any).y1 || 0) - ((d as any).y0 || 0))
      .attr('width', (d) => ((d as any).x1 || 0) - ((d as any).x0 || 0))
      .attr('fill', (d) => {
        const dNode = d as any;
        switch(dNode.type) {
          case 'supplier': return theme.palette.primary.light;
          case 'category': return theme.palette.secondary.light;
          case 'businessUnit': return theme.palette.info.light;
          default: return theme.palette.grey[400];
        }
      })
      .attr('stroke', (d) => {
        const dNode = d as any;
        switch(dNode.type) {
          case 'supplier': return theme.palette.primary.dark;
          case 'category': return theme.palette.secondary.dark;
          case 'businessUnit': return theme.palette.info.dark;
          default: return theme.palette.grey[600];
        }
      })
      .on('mouseover', function(event, d) {
        // Highlight connected links
        const dNode = d as any;
        svg.selectAll('path')
          .attr('stroke-opacity', (p) => {
            const pLink = p as any;
            const source = typeof pLink.source === 'object' ? pLink.source : null;
            const target = typeof pLink.target === 'object' ? pLink.target : null;
            
            const sourceIndex = source ? source.index : pLink.source;
            const targetIndex = target ? target.index : pLink.target;
            
            return (sourceIndex === dNode.index || targetIndex === dNode.index) ? 0.8 : 0.1;
          })
          .attr('stroke-width', (p) => {
            const pLink = p as any;
            const source = typeof pLink.source === 'object' ? pLink.source : null;
            const target = typeof pLink.target === 'object' ? pLink.target : null;
            
            const sourceIndex = source ? source.index : pLink.source;
            const targetIndex = target ? target.index : pLink.target;
            
            return (sourceIndex === dNode.index || targetIndex === dNode.index) ? 
              Math.max(1, (pLink.width || 0) + 2) : 
              Math.max(1, pLink.width || 0);
          });
        
        // Show tooltip
        if (tooltipRef.current) {
          const tooltip = d3.select(tooltipRef.current);
          
          const totalValue = d3.format('$,.0f')(dNode.value || 0);
          const nodeType = dNode.type.charAt(0).toUpperCase() + dNode.type.slice(1);
          
          tooltip.html(`
            <div>
              <strong>${dNode.name}</strong><br/>
              Type: ${nodeType}<br/>
              Total Spend: ${totalValue}
            </div>
          `)
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY + 10}px`)
          .style('opacity', 1);
        }
      })
      .on('mouseout', function() {
        // Reset all links
        svg.selectAll('path')
          .attr('stroke-opacity', 0.5)
          .attr('stroke-width', (d) => Math.max(1, (d as any).width || 0));
          
        // Hide tooltip
        if (tooltipRef.current) {
          d3.select(tooltipRef.current).style('opacity', 0);
        }
      });

    // Add labels to the nodes
    nodeGroup.append('text')
      .attr('x', (d) => {
        const dNode = d as any;
        if (dNode.type === 'supplier') return ((dNode.x1 || 0) - (dNode.x0 || 0)) + 6;
        if (dNode.type === 'businessUnit') return -6;
        return ((dNode.x1 || 0) - (dNode.x0 || 0)) / 2;
      })
      .attr('y', (d) => {
        const dNode = d as any;
        return ((dNode.y1 || 0) - (dNode.y0 || 0)) / 2;
      })
      .attr('dy', '0.35em')
      .attr('text-anchor', (d) => {
        const dNode = d as any;
        if (dNode.type === 'supplier') return 'start';
        if (dNode.type === 'businessUnit') return 'end';
        return 'middle';
      })
      .attr('font-size', '10px')
      .attr('fill', theme.palette.text.secondary)
      .text((d) => {
        const dNode = d as any;
        // If the node is very small, don't show label
        if ((dNode.y1 || 0) - (dNode.y0 || 0) < 20) return '';
        
        // Truncate long names
        const name = dNode.name;
        return name.length > 15 ? name.substring(0, 12) + '...' : name;
      });
      
    // Add a legend
    const legend = svg.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${width - 160}, ${height - 30})`);
    
    legend.append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 20)
      .attr('y2', 0)
      .attr('stroke', theme.palette.error.main)
      .attr('stroke-width', 3)
      .attr('stroke-opacity', 0.8);
    
    legend.append('text')
      .attr('x', 25)
      .attr('y', 4)
      .attr('font-size', '10px')
      .attr('fill', theme.palette.text.secondary)
      .text('Fragmented Spend Path');

    // Cleanup function
    return () => {
      // Clean up tooltip
      if (tooltipRef.current) {
        d3.select(tooltipRef.current).remove();
        // Create a new variable to avoid direct assignment
        const nullTooltip: HTMLDivElement | null = null;
        tooltipRef.current = nullTooltip;
      }
    };
  }, [data, theme]);

  // Process data into Sankey format
  const processDataForSankey = (data: any[]) => {
    // Create maps to track unique entities
    const supplierMap = new Map();
    const categoryMap = new Map();
    const businessUnitMap = new Map();
    
    // Create links by aggregating spend between supplier->category and category->business unit
    const supplierCategoryLinks: Record<string, number> = {};
    const categoryBusinessUnitLinks: Record<string, number> = {};
    
    // Process each data item
    data.forEach(item => {
      const supplierId = item.supplierId || `supplier-${item.supplier}`;
      const supplierName = item.supplier || 'Unknown Supplier';
      const categoryName = item.category || 'Uncategorized';
      const businessUnitName = item.businessUnit || 'Unknown BU';
      const amount = parseFloat(item.amount) || 0;
      
      // Add to supplier map
      if (!supplierMap.has(supplierId)) {
        supplierMap.set(supplierId, {
          id: supplierId,
          name: supplierName,
          type: 'supplier' as const
        });
      }
      
      // Add to category map
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, {
          id: categoryName,
          name: categoryName,
          type: 'category' as const
        });
      }
      
      // Add to business unit map
      if (!businessUnitMap.has(businessUnitName)) {
        businessUnitMap.set(businessUnitName, {
          id: businessUnitName,
          name: businessUnitName,
          type: 'businessUnit' as const
        });
      }
      
      // Create supplier -> category link key
      const supplierCategoryKey = `${supplierId}->${categoryName}`;
      supplierCategoryLinks[supplierCategoryKey] = (supplierCategoryLinks[supplierCategoryKey] || 0) + amount;
      
      // Create category -> business unit link key
      const categoryBusinessUnitKey = `${categoryName}->${businessUnitName}`;
      categoryBusinessUnitLinks[categoryBusinessUnitKey] = (categoryBusinessUnitLinks[categoryBusinessUnitKey] || 0) + amount;
    });
    
    // Create nodes array
    const nodes: SankeyNode[] = [
      ...Array.from(supplierMap.values()),
      ...Array.from(categoryMap.values()),
      ...Array.from(businessUnitMap.values())
    ];
    
    // Create links array
    const links: SankeyLink[] = [];
    
    // Add supplier -> category links
    Object.entries(supplierCategoryLinks).forEach(([key, value], index) => {
      const [sourceId, targetName] = key.split('->');
      const source = nodes.findIndex(node => node.id === sourceId);
      const target = nodes.findIndex(node => node.name === targetName && node.type === 'category');
      
      if (source !== -1 && target !== -1) {
        links.push({
          source,
          target,
          value,
          uValue: index // unique identifier for the link
        });
      }
    });
    
    // Add category -> business unit links
    Object.entries(categoryBusinessUnitLinks).forEach(([key, value], index) => {
      const [sourceName, targetName] = key.split('->');
      const source = nodes.findIndex(node => node.name === sourceName && node.type === 'category');
      const target = nodes.findIndex(node => node.name === targetName && node.type === 'businessUnit');
      
      if (source !== -1 && target !== -1) {
        links.push({
          source,
          target,
          value,
          uValue: index + Object.keys(supplierCategoryLinks).length // ensure unique identifier
        });
      }
    });
    
    return { nodes, links };
  };

  // Identify fragmented links
  const identifyFragmentedLinks = (links: SankeyLink[]) => {
    // Group links by source
    const linksBySource: Record<string, SankeyLink[]> = {};
    links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.index?.toString() : link.source.toString();
      if (sourceId) {
        if (!linksBySource[sourceId]) {
          linksBySource[sourceId] = [];
        }
        linksBySource[sourceId].push(link);
      }
    });
    
    // Group links by target
    const linksByTarget: Record<string, SankeyLink[]> = {};
    links.forEach(link => {
      const targetId = typeof link.target === 'object' ? link.target.index?.toString() : link.target.toString();
      if (targetId) {
        if (!linksByTarget[targetId]) {
          linksByTarget[targetId] = [];
        }
        linksByTarget[targetId].push(link);
      }
    });
    
    // Mark links as fragmented if source has multiple targets or target has multiple sources
    links.forEach(link => {
      const sourceId = typeof link.source === 'object' ? link.source.index?.toString() : link.source.toString();
      const targetId = typeof link.target === 'object' ? link.target.index?.toString() : link.target.toString();
      
      if (sourceId && targetId) {
        // If a source has multiple targets or a target has multiple sources and
        // the value is significant (more than 10% of the total), mark as fragmented
        const isSourceFragmented = linksBySource[sourceId] && linksBySource[sourceId].length > 1;
        const isTargetFragmented = linksByTarget[targetId] && linksByTarget[targetId].length > 1;
        
        // Calculate total value for source and target
        const sourceTotal = linksBySource[sourceId]?.reduce((sum: number, l: SankeyLink) => sum + l.value, 0) || 0;
        const targetTotal = linksByTarget[targetId]?.reduce((sum: number, l: SankeyLink) => sum + l.value, 0) || 0;
        
        // Calculate the percentage of this link relative to the total
        const sourcePercentage = sourceTotal > 0 ? (link.value / sourceTotal) : 0;
        const targetPercentage = targetTotal > 0 ? (link.value / targetTotal) : 0;
        
        // Set as fragmented if it's a significant flow (> 15% of total)
        link.isFragmented = (isSourceFragmented && sourcePercentage > 0.15) || 
                          (isTargetFragmented && targetPercentage > 0.15);
      }
    });
  };

  return (
    <Box sx={{ width: '100%', height: 600, position: 'relative' }}>
      {!data || data.length === 0 ? (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            border: '1px dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 3
          }}
        >
          <Typography color="text.secondary">
            No data available for Spend Cube visualisation.
          </Typography>
        </Box>
      ) : (
        <Box 
          ref={containerRef} 
          sx={{ 
            width: '100%', 
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden'
          }}
        />
      )}
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        This Sankey diagram shows the flow of spend from Suppliers (left) through Categories (middle) to Business Units (right).
        Red lines indicate fragmented spend paths that may present consolidation opportunities.
      </Typography>
    </Box>
  );
};

export default SpendCubeChart; 